/**
 * Conditional (if-else) node component for React Flow
 */

import { memo } from 'react';
import { Handle, Position } from 'reactflow';
import './ConditionalNode.css';

interface ConditionalNodeData {
  label: string;
  condition?: {
    upstream_step_reference: string;
    field: string;
    operator: string;
    value: any;
  };
  status?: 'pending' | 'running' | 'completed' | 'failed';
}

interface ConditionalNodeProps {
  data: ConditionalNodeData;
  selected?: boolean;
}

export const ConditionalNode = memo<ConditionalNodeProps>(({ data, selected }) => {
  return (
    <div className={`conditional-node ${selected ? 'selected' : ''} ${data.status || 'pending'}`}>
      {data.status && data.status !== 'pending' && (
        <div className={`node-status-badge status-${data.status}`}>
          {data.status === 'running' && (
            <svg className="node-status-badge-spinner" width="13" height="13" viewBox="0 0 24 24">
              <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="3" fill="none" strokeDasharray="40" strokeDashoffset="10" strokeLinecap="round" />
            </svg>
          )}
          {data.status === 'completed' && (
            <svg width="13" height="13" viewBox="0 0 16 16" fill="none">
              <path d="M3 8.5L6.5 12L13 4" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          )}
          {data.status === 'failed' && (
            <svg width="13" height="13" viewBox="0 0 16 16" fill="none">
              <path d="M4 4L12 12M12 4L4 12" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
            </svg>
          )}
        </div>
      )}
      <Handle type="target" position={Position.Top} className="conditional-node-handle" />

      <div className="conditional-node-header">
        <div className="conditional-node-icon">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
            <path d="M10 2L15 8H5L10 2Z" />
            <path d="M10 18L5 12H15L10 18Z" />
          </svg>
        </div>
        <div className="conditional-node-title">{data.label}</div>
      </div>

      <div className="conditional-node-body">
        <div className="conditional-node-type">Branch (If / Else)</div>
        {data.condition ? (
          <div className="conditional-node-conditions">
            <div className="condition-item">
              <span className="condition-label">If:</span>
              <span className="condition-text">
                {data.condition.upstream_step_reference}.{data.condition.field} {data.condition.operator} {JSON.stringify(data.condition.value)}
              </span>
            </div>
          </div>
        ) : (
          <div className="conditional-node-conditions">
            <span className="condition-text text-secondary">No condition configured</span>
          </div>
        )}
      </div>

      <div className="conditional-node-outputs">
        <span className="conditional-node-output-label conditional-node-output-true">True</span>
        <span className="conditional-node-output-label conditional-node-output-false">False</span>
      </div>

      <Handle
        type="source"
        position={Position.Bottom}
        id="true"
        className="conditional-node-handle conditional-node-handle-true"
        style={{ left: '30%' }}
      />
      <Handle
        type="source"
        position={Position.Bottom}
        id="false"
        className="conditional-node-handle conditional-node-handle-false"
        style={{ left: '70%' }}
      />
    </div>
  );
});

ConditionalNode.displayName = 'ConditionalNode';
